import React, { useEffect, useRef, useState } from "react";
import { ArrowLeft, Smartphone } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import Logo from "../components/Logo";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";

export default function OtpVerify() {
  const nav = useNavigate();
  const { verifyOtp } = useAuth();
  const [params] = useSearchParams();
  const phone = params.get("phone") || "";
  const [digits, setDigits] = useState(["", "", "", "", "", ""]);
  const [busy, setBusy] = useState(false);
  const [seconds, setSeconds] = useState(45);
  const refs = useRef([]);

  useEffect(() => {
    if (seconds <= 0) return;
    const t = setTimeout(() => setSeconds(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [seconds]);

  const set = (i, v) => {
    if (!/^\d?$/.test(v)) return;
    const next = [...digits]; next[i] = v; setDigits(next);
    if (v && i < 5) refs.current[i + 1]?.focus();
  };

  const submit = async (e) => {
    e.preventDefault();
    const otp = digits.join("");
    if (otp.length < 6) return;
    setBusy(true);
    const r = await verifyOtp(phone, otp);
    setBusy(false);
    if (!r.ok) return toast.error(r.error);
    toast.success("Verified! Welcome to Orello.");
    // Navigate to dashboard
    // The popstate listener in AuthContext will prevent going back
    nav("/app");
  };

  const resend = async () => {
    try { await api.post("/auth/resend-otp", { phone }); setSeconds(45); toast.info("OTP sent. Use 123456 (mock)."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="px-6 py-5 flex items-center gap-4 max-w-7xl mx-auto w-full">
        <Link to="/register" className="text-gray-500 hover:text-gray-900"><ArrowLeft className="h-5 w-5" /></Link>
        <Link to="/"><Logo size="sm" /></Link>
      </header>

      <div className="flex-1 flex items-center justify-center px-4">
        <div className="w-full max-w-md card-base p-8" data-testid="otp-page">
          <div className="h-14 w-14 rounded-2xl bg-[#FFF4ED] text-[#F97316] flex items-center justify-center mb-5"><Smartphone className="h-7 w-7" /></div>
          <h1 className="text-2xl font-bold font-display">Verify your number</h1>
          <p className="text-sm text-gray-500 mt-1">We sent a 6-digit code via SMS to <span className="font-medium text-gray-700">{phone.slice(0, -4).replace(/./g, "•")}{"•"}{phone.slice(-4)}</span></p>
          <p className="text-xs text-gray-400 mt-1">The same 6-digit code was also sent to your email.</p>

          <form onSubmit={submit} className="mt-6 space-y-5">
            <div className="flex gap-2">
              {digits.map((d, i) => (
                <input key={i} ref={(el) => (refs.current[i] = el)} value={d} onChange={(e) => set(i, e.target.value)}
                  data-testid={`otp-${i}`} maxLength={1} inputMode="numeric"
                  className={`w-12 h-14 text-center text-lg font-semibold border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-200 ${d ? "border-indigo-500" : "border-gray-300"}`} />
              ))}
            </div>
            <div className="text-sm text-gray-500">
              {seconds > 0 ? <>Didn't get it? Resend in 0:{String(seconds).padStart(2, "0")}</> : <button type="button" onClick={resend} className="text-[#F97316] font-medium">Resend OTP</button>}
            </div>
            <button data-testid="otp-submit" disabled={busy || digits.join("").length < 6} className="w-full h-12 bg-[#F97316] hover:bg-[#EA580C] btn-press btn-orange-primary text-white rounded-lg font-medium disabled:opacity-50">
              {busy ? "Verifying…" : "Verify"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
